import { runInAction } from "mobx";
import type { Segment, FetchByQueryResult } from "mobx-repository";
import { prop } from "ramda";
import type {
    EloshieldClassicStationaryModule,
    EloshieldClassicStationaryModuleCreate,
    EloshieldClassicStationaryModulesApi,
    EloshieldClassicStationaryModulesListEloshieldClassicStationaryModulesRequest,
    EloshieldClassicStationaryModuleSortKey,
    EloshieldClassicStationaryModuleUpdate,
    PageInfo,
} from "src/api";
import { defaultPageSize } from "../../utils/constants";
import type { ApiResource } from "../../utils/elofleet-repository";
import { ElofleetRepository } from "../../utils/elofleet-repository";
import type { UUID } from "../../utils/uuid";
import { Validation } from "../../utils/validation";

export type EloshieldClassicStationaryModulesQuery = Omit<
    EloshieldClassicStationaryModulesListEloshieldClassicStationaryModulesRequest,
    "page"
>;

export interface EloshieldClassicStationaryModulesApiResource extends ApiResource {
    entity: EloshieldClassicStationaryModule;
    query: EloshieldClassicStationaryModulesQuery;
    update: EloshieldClassicStationaryModuleUpdate;
    create: EloshieldClassicStationaryModuleCreate;
    sortKey: EloshieldClassicStationaryModuleSortKey;
}

export class RepositoryEloshieldClassicStationaryModules extends ElofleetRepository<EloshieldClassicStationaryModulesApiResource> {
    constructor(
        private readonly eloshieldClassicStationaryModulesApi: EloshieldClassicStationaryModulesApi,
    ) {
        super();
    }

    public validation = {
        create: new Validation(
            (eloshieldClassicStationaryModuleCreate: EloshieldClassicStationaryModuleCreate) =>
                this.eloshieldClassicStationaryModulesApi.eloshieldClassicStationaryModulesValidateCreateEloshieldClassicStationaryModule(
                    {
                        eloshieldClassicStationaryModuleCreate:
                            eloshieldClassicStationaryModuleCreate,
                    },
                ),
        ),
        update: new Validation(
            (
                eloshieldClassicStationaryModuleUpdate: EloshieldClassicStationaryModuleUpdate,
                id: UUID,
            ) =>
                this.eloshieldClassicStationaryModulesApi.eloshieldClassicStationaryModulesValidateUpdateEloshieldClassicStationaryModule(
                    {
                        eloshieldClassicStationaryModuleUpdate,
                        id,
                    },
                ),
        ),
    };

    protected extractId = prop("id");

    protected async fetchById(id: string): Promise<EloshieldClassicStationaryModule | undefined> {
        return await this.eloshieldClassicStationaryModulesApi.eloshieldClassicStationaryModulesReadEloshieldClassicStationaryModule(
            { id },
        );
    }

    protected async fetchByQuery(
        query: EloshieldClassicStationaryModulesQuery,
        pagination: Segment,
    ): Promise<FetchByQueryResult<EloshieldClassicStationaryModule>> {
        const page = pagination.offset / this.defaultCount;
        const entities =
            await this.eloshieldClassicStationaryModulesApi.eloshieldClassicStationaryModulesListEloshieldClassicStationaryModules(
                { ...query, page },
            );
        return { entities };
    }

    public fetchMetadata(query: EloshieldClassicStationaryModulesQuery): Promise<PageInfo> {
        return this.eloshieldClassicStationaryModulesApi.eloshieldClassicStationaryModulesListEloshieldClassicStationaryModulesMeta(
            query,
        );
    }

    public async create(
        eloshieldClassicStationaryModuleCreate: EloshieldClassicStationaryModuleCreate,
    ): Promise<EloshieldClassicStationaryModule> {
        const entity =
            await this.eloshieldClassicStationaryModulesApi.eloshieldClassicStationaryModulesCreateEloshieldClassicStationaryModule(
                { eloshieldClassicStationaryModuleCreate },
            );
        await this.waitForIdle();
        runInAction(() => {
            // This reload is necessary to reload all queries that want to display "all" entities.
            this.reloadQuery({ pageSize: defaultPageSize });
            this.add(entity);
        });

        return entity;
    }

    public async update(
        id: UUID,
        eloshieldClassicStationaryModuleUpdate: EloshieldClassicStationaryModuleUpdate,
    ): Promise<EloshieldClassicStationaryModule> {
        const entity =
            await this.eloshieldClassicStationaryModulesApi.eloshieldClassicStationaryModulesUpdateEloshieldClassicStationaryModule(
                { id, eloshieldClassicStationaryModuleUpdate },
            );
        await this.waitForIdle();
        runInAction(() => {
            // This reload is necessary to reload all queries that want to display "all" entities.
            this.reloadQuery({ pageSize: defaultPageSize });
            this.add(entity);
        });

        return entity;
    }

    public delete(): Promise<void> {
        throw new Error("Cannot delete ELOshield Classic stationary modules: Not implemented.");
    }
}
